import React, { useEffect, useState } from 'react';
import { Button, Input, OnlinePriceTableChart, Pagination, Table } from '../components';
import { AiFillAppstore } from 'react-icons/ai';
import { CiSearch } from 'react-icons/ci';
import { ColumnType } from 'antd/lib/table';
import { TiArrowDown, TiArrowUp } from 'react-icons/ti';
import CoinIcon from '../../utilities/components/coinIcon/coinIcon.index';
import { FiatType, Market } from 'interfaces';
import { getFiats, getPrices } from './markets.service';
import { FaRegHeart, FaRegStar } from 'react-icons/fa';
import { TbArrowsTransferDown, TbArrowsTransferUp } from 'react-icons/tb';
import { Divider } from 'antd';
import { useNavigate } from 'react-router-dom';

const staticSorts = [
    { id: 0, title: 'همه ارزها' },
    { id: 1, title: 'جدیدترین‌ها' },
    { id: 2, title: 'بیشترین سود' },
    { id: 3, title: 'بیشترین ضرر' },
    { id: 4, title: 'محبوب‌ترین‌ها' },
];

interface Props {
    isPreview?: boolean;
}

export const Markets: React.FC<Props> = (props) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [data, setData] = useState<Market[]>([]);
    const [fiats, setFiats] = useState<FiatType[]>([]);
    const [filteredData, setFilteredData] = useState<Market[]>(data);
    const [keyword, setKeyword] = useState<string>('');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(10);
    const [selectedSort, setSelectedSort] = useState<number | null>(null);
    const [selectedType, setSelectedType] = useState<number | null>(null);

    const navigate = useNavigate();

    const columns: ColumnType<any>[] = [
        {
            title: 'ردیف',
            dataIndex: 'id',
            className: props.isPreview ? 'preview' : '',
            key: 'id',
            render: (text, record, index) => <span className={'font-medium text-sm'}>{index + 1}</span>,
            width: props.isPreview ? 64 : undefined,
        },
        {
            title: 'نام ارز',
            dataIndex: 'name',
            key: 'name',
            className: props.isPreview ? 'preview' : '',
            render: (text, record) => (
                <div className="flex items-center justify-between gap-2">
                    <div className={`flex items-center whitespace-nowrap ${props.isPreview ? 'gap-1' : 'gap-3'}`}>
                        <CoinIcon symbol={record.symbol} size={props.isPreview ? 'small' : 'default'} />
                        <span className={`${props.isPreview ? 'text-xs' : 'text-sm'} font-bold`}>
                            {text?.toUpperCase()}
                        </span>
                        <span className={'text-xs font-medium'}>{record.symbol?.toUpperCase()}</span>
                    </div>
                    {renderDiscountTag(record.symbol)}
                </div>
            ),
        },
        {
            title: 'قیمت دلاری',
            dataIndex: 'inDollarPrice',
            className: props.isPreview ? 'preview' : '',
            key: 'inDollarPrice',
            render: (text) => (
                <span className={`font-medium ${props.isPreview ? 'text-xs' : 'text-sm'}`}>
                    {text.toLocaleString()} دلار
                </span>
            ),
        },
        {
            title: 'قیمت خرید',
            dataIndex: 'buyPrice',
            key: 'buyPrice',
            render: (text) => (
                <span className={`font-medium ${props.isPreview ? 'text-xs' : 'text-sm'}`}>
                    {text.toLocaleString()} تومان
                </span>
            ),
            className: props.isPreview ? 'preview' : '',
        },
        {
            title: 'قیمت فروش',
            dataIndex: 'sellPrice',
            key: 'sellPrice',
            render: (text) => (
                <span className={`font-medium ${props.isPreview ? 'text-xs' : 'text-sm'}`}>
                    {text.toLocaleString()} تومان
                </span>
            ),
            className: props.isPreview ? 'preview' : '',
        },
        ...(!props.isPreview
            ? [
                  {
                      title: props.isPreview ? 'تغییرات' : 'درصد تغییرات',
                      dataIndex: 'changeOf24HourPercent',
                      key: 'changeOf24HourPercent',
                      render: (text: number, record: Market) => (
                          <span
                              className={`flex items-center gap-1 font-medium ${props.isPreview ? 'text-xs' : 'text-sm'} ${text > 0 ? 'text-positive-dark' : 'text-negative-dark'}`}
                          >
                              {['UUSD', 'USDT'].includes(record.symbol) ? (
                                  <div className="w-full flex justify-center">-</div>
                              ) : (
                                  <>
                                      {Math.abs(Number(text))?.toFixed(2)}٪{text > 0 ? <TiArrowUp /> : <TiArrowDown />}
                                  </>
                              )}
                          </span>
                      ),
                      className: props.isPreview ? 'preview' : '',
                  },
              ]
            : []),
        ...(!props.isPreview
            ? [
                  {
                      title: 'نمودار ۲۴ ساعته',
                      dataIndex: 'weeklyPriceLog',
                      key: 'weeklyPriceLog',
                      render: (chartData: number[]) => {
                          return <OnlinePriceTableChart data={chartData} />;
                      },
                  },
              ]
            : []),
        {
            title: 'معامله',
            dataIndex: 'details',
            align: 'center',
            key: 'details',
            render: (text, row) => (
                <Button
                    className={'h-8 text-sm'}
                    onClick={() => navigate(`/trade?symbol=${row.symbol?.toUpperCase()}`)}
                >
                    معامله
                </Button>
            ),
            className: props.isPreview ? 'preview' : '',
        },
    ];

    useEffect(() => {
        getPrices(setLoading, setData, 1, props.isPreview ? 8 : 999);
        getFiats(setLoading, setFiats);
    }, []);

    useEffect(() => {
        setFilteredData(data);
    }, [data]);

    const renderTypeIcon = (id: number) => {
        switch (id) {
            case 0:
                return <AiFillAppstore />;
            case 1:
                return <FaRegStar />;
            case 2:
                return <TbArrowsTransferUp />;
            case 3:
                return <TbArrowsTransferDown />;
            case 4:
                return <FaRegHeart />;
            default:
                return null;
        }
    };

    const filterData = () => {
        let newData = [...data];

        if (selectedSort === 2) {
            newData = newData.sort((a, b) => b.changeOf24HourPercent - a.changeOf24HourPercent);
        } else if (selectedSort === 3) {
            newData = newData.sort((a, b) => a.changeOf24HourPercent - b.changeOf24HourPercent);
        }

        if (keyword) {
            newData = newData.filter(
                (item) => item.name.includes(keyword) || item.symbol.toLowerCase().includes(keyword.toLowerCase()),
            );
        }

        setFilteredData(newData);
    };

    const handleKeywordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setKeyword(event.target.value);
    };

    const handleSortChange = (id: number) => {
        setSelectedSort(id === selectedSort ? null : id);
        setCurrentPage(1);
    };

    const handleTypeToggle = (id: number) => {
        setSelectedType(id === selectedType ? null : id);
        filterData();
    };

    useEffect(() => {
        filterData();
        console.log({ fiats });
    }, [keyword, selectedSort]);

    const renderDiscountTag = (symbol?: string) => {
        if (!symbol) return null;

        const fiat = fiats.find((item) => item.symbol?.toUpperCase() === symbol.toUpperCase());
        if (!fiat || !fiat.sellDiscount) return null;

        return (
            <span className="h-5 px-2 flex items-center justify-center rounded-lg bg-[#2AC7691A] text-[#2AC769] border border-[#2AC76966] text-xs font-medium whitespace-nowrap">
                تخفیف {fiat.sellDiscount} درصد
            </span>
        );
    };

    return (
        <div className={`${props.isPreview ? '' : 'p-6'} w-full h-full`}>
            {!props.isPreview && (
                <>
                    <div className="flex gap-4 items-center overflow-x-scroll scrollbar-hidden max-lg:gap-2">
                        {staticSorts.map((sort) => (
                            <Button
                                key={sort.id}
                                icon={renderTypeIcon(sort.id)}
                                size="small"
                                className={'!h-12'}
                                type={selectedSort === sort.id ? 'primary' : 'default'}
                                onClick={() => handleSortChange(sort.id)}
                            >
                                {sort.title}
                            </Button>
                        ))}
                    </div>
                    <Input
                        className={'mt-6 h-12 w-72 max-lg:w-full'}
                        size={'small'}
                        placeholder={'ارز مورد نظر خود را جستجو کنید'}
                        prefix={<CiSearch />}
                        value={keyword}
                        onChange={handleKeywordChange}
                    />

                    {/* <div
                        className={'flex justify-between items-center mt-6 w-full bg-white h-14 px-4 border border-primary-20 rounded-t-[8px] max-lg:rounded-[8px] max-lg:overflow-x-scroll scrollbar-hidden max-lg:gap-4'}>
                        <div className={'flex items-center gap-4 text-sm font-medium relative h-full'}>
                            {staticTypes.map(type => (
                                <div key={type.id}
                                     className="relative clickable h-full flex items-center max-lg:whitespace-nowrap"
                                     onClick={() => handleTypeToggle(type.id)}>
                            <span
                                className={`py-2 px-4 transition duration-300 ease-in-out ${selectedType === type.id ? 'text-black-100' : 'text-black-60'}`}>
                                {type.title}
                            </span>
                                    {selectedType === type.id && (
                                        <div
                                            className="absolute left-0 right-0 bottom-0 h-1 bg-primary-100 rounded-t-[2.5px]" />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div> */}
                </>
            )}
            <Table
                className={`max-lg:hidden test ${props.isPreview ? 'overflow-auto table-preview' : 'mt-6'}`}
                columns={columns}
                dataSource={filteredData}
                loading={loading}
                rowKey="id"
                pagination={
                    !props.isPreview
                        ? {
                              current: currentPage,
                              pageSize: itemsPerPage,
                              total: filteredData.length,
                              onChange: (page) => setCurrentPage(page),
                              onShowSizeChange: (current, size) => {
                                  setItemsPerPage(size);
                                  setCurrentPage(1);
                              },
                              showSizeChanger: true,
                              position: ['bottomCenter'],
                          }
                        : false
                }
            />

            <ul className={`flex flex-col gap-4 ${props.isPreview ? '' : 'mt-4'} lg:hidden bg-background`}>
                {filteredData.map((item) => (
                    <li className={'p-4 bg-white'} key={item.id}>
                        <div className="flex items-center justify-between" id="here">
                            <div className={'flex items-center gap-2'}>
                                <CoinIcon symbol={item?.symbol} className={'w-8 h-8'} />
                                <span className={'text-sm font-bold'}>{item.name}</span>
                                <span className={'text-xs font-medium'}>{item?.symbol?.toUpperCase()}</span>
                            </div>
                            {renderDiscountTag(item.symbol)}
                        </div>
                        <Divider className={'my-3'} />
                        <div className={'flex justify-between items-center'}>
                            <div className={'flex flex-col'}>
                                <p className={'text-sm font-bold'}>قیمت دلاری:</p>
                                <span className={'text-sm font-medium text-darkPurple-60'}>
                                    {item.inDollarPrice.toLocaleString()} دلار
                                </span>
                            </div>
                            <div className={'flex flex-col'}>
                                <p className={'text-sm font-bold'}>قیمت خرید:</p>
                                <span className={'text-sm font-medium text-darkPurple-60'}>
                                    {item.buyPrice.toLocaleString()} تومان
                                </span>
                            </div>
                            <div className={'flex flex-col'}>
                                <p className={'text-sm font-bold'}>قیمت فروش:</p>
                                <span className={'text-sm font-medium text-darkPurple-60'}>
                                    {item.sellPrice.toLocaleString()} تومان
                                </span>
                            </div>
                        </div>
                        <div className={'flex justify-between mt-4 items-center'}>
                            <div className={'flex flex-col'}>
                                <span
                                    className={`flex items-center gap-1 font-medium text-sm ${Number(item.changeOf24HourPercent) > 0 ? 'text-positive-dark' : 'text-negative-dark'}`}
                                >
                                    {Math.abs(Number(item.changeOf24HourPercent))?.toFixed(2)}٪
                                    {Number(item.changeOf24HourPercent) > 0 ? <TiArrowUp /> : <TiArrowDown />}
                                </span>
                            </div>
                            <div className={'flex flex-col'}>
                                <OnlinePriceTableChart data={item.weeklyPriceLog} />
                            </div>
                            <div className={'flex flex-col'}>
                                <Button
                                    className={'!h-8 text-sm'}
                                    onClick={() => navigate(`/trade?symbol=${item.symbol?.toUpperCase()}`)}
                                >
                                    معامله
                                </Button>
                            </div>
                        </div>
                    </li>
                ))}
            </ul>

            <Pagination
                current={currentPage}
                pageSize={itemsPerPage}
                total={filteredData.length}
                onChange={(page) => setCurrentPage(page)}
                onShowSizeChange={(current, size) => {
                    setItemsPerPage(size);
                    setCurrentPage(1);
                }}
                className={`mt-4 justify-center lg:hidden mx-auto ${props.isPreview ? 'hidden' : ''}`}
                style={{ direction: 'ltr' }}
                showSizeChanger
            />
        </div>
    );
};
